import { zodResolver } from '@hookform/resolvers/zod';
import { DialogTitle } from '@savvy-kit/components/dialog';
import IconButton from '@savvy-kit/components/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Text } from '@savvy-kit/foundations/text';
import InputField from '@savvy-kit/patterns/input-field';
import { Trash2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { z } from 'zod';

import type { AccountRow } from './types';
import { CLIENT_PORTAL_DIALOG_PANEL_CLASS } from '../common/constants';
import { AddAccountFormShell } from './add-account/add-account-form-shell';
import {
  ACCOUNTS_RENAME_CLEAR_LABEL,
  ACCOUNTS_RENAME_DESCRIPTION,
  ACCOUNTS_RENAME_FIELD_LABEL,
  ACCOUNTS_RENAME_MAX_LENGTH,
  ACCOUNTS_RENAME_SUBMIT_LABEL,
  ACCOUNTS_RENAME_TITLE,
} from './constants';

const renameSchema = z.object({
  customName: z
    .string()
    .trim()
    .max(ACCOUNTS_RENAME_MAX_LENGTH, `Keep it under ${ACCOUNTS_RENAME_MAX_LENGTH} characters`),
});

type RenameFormValues = z.infer<typeof renameSchema>;

type AccountsRenameDialogProps = {
  readonly row: AccountRow;
  readonly onRename: (row: AccountRow, customName: string | null) => Promise<void>;
  readonly onClose: () => void;
};

export function AccountsRenameDialog({ row, onRename, onClose }: AccountsRenameDialogProps) {
  const { control, handleSubmit, formState } = useForm<RenameFormValues>({
    resolver: zodResolver(renameSchema),
    defaultValues: { customName: row.customName ?? '' },
  });

  function write(customName: string | null) {
    onClose();
    void onRename(row, customName);
  }

  const submit = handleSubmit((values) => {
    const trimmed = values.customName.trim();
    write(trimmed === '' ? null : trimmed);
  });

  return (
    <TrackedDialog
      elementName="rename-account-dialog"
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      className={CLIENT_PORTAL_DIALOG_PANEL_CLASS}
    >
      <DialogTitle className="sr-only">{ACCOUNTS_RENAME_TITLE}</DialogTitle>
      <AddAccountFormShell
        title={ACCOUNTS_RENAME_TITLE}
        onCancel={onClose}
        onSubmit={submit}
        submitLabel={ACCOUNTS_RENAME_SUBMIT_LABEL}
        leadingAction={
          row.customName !== null && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={ACCOUNTS_RENAME_CLEAR_LABEL}
                    onClick={() => {
                      write(null);
                    }}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </IconButton>
                }
              />
              <TooltipContent>{ACCOUNTS_RENAME_CLEAR_LABEL}</TooltipContent>
            </Tooltip>
          )
        }
      >
        <Text variant="body-sm" className="sr-only md:not-sr-only">
          {ACCOUNTS_RENAME_DESCRIPTION.replace('{name}', row.sourceName)}
        </Text>
        <Controller
          control={control}
          name="customName"
          render={({ field }) => (
            <InputField
              {...field}
              id="client-portal-account-custom-name"
              label={ACCOUNTS_RENAME_FIELD_LABEL}
              autoFocus
              maxLength={ACCOUNTS_RENAME_MAX_LENGTH}
              error={formState.errors.customName !== undefined}
              errorMessage={formState.errors.customName?.message}
            />
          )}
        />
      </AddAccountFormShell>
    </TrackedDialog>
  );
}
